import { useApolloClient } from '@apollo/client';
import { useEffect, useState } from 'react';

import * as graphql from '@treams/treams-network';

const PAGE_SIZE = 100;

export type TalentReviewGroup = graphql.TalentReviewGroupsQuery['groups']['edges'][number]['node'];

/**
 * Loads every non-archived department and team in pages of 100, like the
 * goals group selector does. The list resolves members per group, so one
 * request for the whole workspace is too heavy and the connection has no
 * "all" mode.
 *
 * Returns `undefined` until the last page is in.
 */
export default function useBatchedTalentReviewGroups(): TalentReviewGroup[] | undefined {
	const client = useApolloClient();
	const [groups, setGroups] = useState<TalentReviewGroup[] | undefined>(undefined);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			const loaded: TalentReviewGroup[] = [];
			let after: string | undefined;

			do {
				const batch = await client.query<
					graphql.TalentReviewGroupsQuery,
					graphql.TalentReviewGroupsQueryVariables
				>({
					query: graphql.TalentReviewGroupsDocument,
					variables: {
						first: PAGE_SIZE,
						after,
						filter: {
							isArchived: false,
							types: [graphql.GroupType.Department, graphql.GroupType.Team],
						},
					},
				});

				loaded.push(...batch.data.groups.edges.map(({ node }) => node));

				const { pageInfo } = batch.data.groups;
				after = pageInfo.hasNextPage ? (pageInfo.endCursor ?? undefined) : undefined;
			} while (after && !cancelled);

			if (!cancelled) {
				setGroups(loaded);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [client]);

	return groups;
}
